/*
ChromakeyGUIHelper.js
=====================
Utility functions for binding Tweakpane GUI controls to ChromakeyMaterial.
Extracted from VideoPlane.js to be reusable across different components.
*/

/**
 * Binds Tweakpane controls to a ChromakeyMaterial
 * @param {Pane} pane - Tweakpane instance
 * @param {ChromakeyMaterial} material - The chroma key material to control
 * @param {Function} onChange - Optional callback when any setting changes
 * @returns {Object} The created folder for later cleanup
 */
export function bindChromakeyControls(pane, material, onChange = null) {
  const folder = pane.addFolder({ title: 'Chroma Key Settings', expanded: true })

  // Key Color
  folder
    .addInput(material, 'keyColor', {
      label: 'Key Color',
      color: { type: 'float' },
    })
    .on('change', ev => {
      material.convertGammaToLinear(ev.value)
      if (onChange) onChange()
    })

  // Color Cutoff
  folder
    .addInput(material.uniforms._colorCutoff, 'value', {
      label: 'Color Cutoff',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Color Feathering
  folder
    .addInput(material.uniforms._colorFeathering, 'value', {
      label: 'Color Feathering',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Mask Feathering
  folder
    .addInput(material.uniforms._maskFeathering, 'value', {
      label: 'Mask Feathering',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Sharpening
  folder
    .addInput(material.uniforms._sharpening, 'value', {
      label: 'Sharpening',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Despill
  folder
    .addInput(material.uniforms._despill, 'value', {
      label: 'Despill',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Despill Luminance Add
  folder
    .addInput(material.uniforms._despillLuminanceAdd, 'value', {
      label: 'Despill Lum Add',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  // Crop controls in a subfolder
  const cropFolder = folder.addFolder({ title: 'Crop', expanded: false })

  cropFolder
    .addInput(material.crop, 'x', {
      label: 'Top',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  cropFolder
    .addInput(material.crop, 'y', {
      label: 'Bottom',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  cropFolder
    .addInput(material.crop, 'z', {
      label: 'Left',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  cropFolder
    .addInput(material.crop, 'w', {
      label: 'Right',
      min: 0,
      max: 1,
      step: 0.01,
    })
    .on('change', () => {
      if (onChange) onChange()
    })

  return folder
}

/**
 * Extracts chroma settings from a ChromakeyMaterial's uniforms
 * @param {ChromakeyMaterial} material - The material to extract settings from
 * @returns {Object} Object with all chroma settings
 */
export function extractChromaSettings(material) {
  return {
    key_color: rgbToHex(
      material.uniforms._keyColor.value.r * 255,
      material.uniforms._keyColor.value.g * 255,
      material.uniforms._keyColor.value.b * 255
    ),
    cut_off: material.uniforms._colorCutoff.value,
    color_feathering: material.uniforms._colorFeathering.value,
    mask_feathering: material.uniforms._maskFeathering.value,
    sharpening: material.uniforms._sharpening.value,
    despill_strength: material.uniforms._despill.value,
    despill_luminance_add: material.uniforms._despillLuminanceAdd.value,
    crop_top: material.crop.x,
    crop_bottom: material.crop.y,
    crop_left: material.crop.z,
    crop_right: material.crop.w,
  }
}

/**
 * Helper function to convert RGB to hex
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string
 */
function rgbToHex(r, g, b) {
  const toHex = (n) => {
    const hex = Math.round(n).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return '#' + toHex(r) + toHex(g) + toHex(b)
}
